import { formatBytes, formatBitrate, formatTimecode } from '../lib/format';
import { useMountEffect, useStore } from '../lib/hooks';
import { Meter } from './Meter';
import type { EngineState } from '../lib/engine';
import type { ClipPlayer } from '../lib/player';
import type { MeterLevels } from '../lib/audio';
import type { Store } from '../lib/store';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
    </span>
  );
}

export function RecorderDeck({
  state,
  meterStore,
  onRecord,
  onStop,
  onPause,
  onResume,
  onDiscard,
  canRecord,
}: {
  state: EngineState;
  meterStore: Store<MeterLevels>;
  onRecord: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onDiscard: () => void;
  canRecord: boolean;
}) {
  const recording = state.phase === 'recording';
  const paused = state.phase === 'paused';
  const live = recording || paused || state.phase === 'arming';
  const stats = state.stats;

  return (
    <div className="deck">
      <div className="deck__primary">
        <button
          type="button"
          className={`recbutton${live ? ' is-live' : ''}`}
          onClick={live ? onStop : onRecord}
          disabled={!live && !canRecord}
          aria-label={live ? 'Stop recording' : 'Start recording'}
        >
          <span className="recbutton__glyph" />
          <span className="recbutton__label">{live ? 'Stop' : 'Record'}</span>
        </button>
        <button
          type="button"
          className="deck__ghost"
          onClick={recording ? onPause : onResume}
          disabled={!recording && !paused}
        >
          {paused ? 'Resume' : 'Pause'}
        </button>
        <button
          type="button"
          className="deck__ghost deck__ghost--quiet"
          onClick={onDiscard}
          disabled={live}
        >
          Reset
        </button>
      </div>

      <div className="deck__timer">
        <span className={`deck__timer-value${live ? ' is-live' : ''}`}>
          {formatTimecode(state.elapsedUs)}
        </span>
        <span className="deck__timer-label">
          {live ? (paused ? 'paused' : 'elapsed') : state.phase === 'indexing' ? 'indexing' : 'ready'}
        </span>
      </div>

      <div className="deck__stats">
        <Stat label="frames" value={stats ? String(stats.frames) : '—'} />
        <Stat label="dropped" value={stats ? String(stats.dropped) : '—'} />
        <Stat label="size" value={stats ? formatBytes(stats.bytes) : '—'} />
        <Stat label="rate" value={stats ? formatBitrate(stats.bitrate) : '—'} />
        <div className="deck__meters">
          <Meter store={meterStore} label="mic" level="mic" />
          <Meter store={meterStore} label="sys" level="system" />
        </div>
      </div>
    </div>
  );
}

/** Variant for takes large enough that no in-memory preview was built. */
export function EditorDeckFallback({
  durationUs,
  onNewTake,
}: {
  durationUs: number;
  onNewTake: () => void;
}) {
  return (
    <div className="deck deck--editor">
      <div className="deck__primary">
        <span className="deck__ghost is-static">no preview</span>
      </div>
      <div className="deck__timer">
        <span className="deck__timer-value">
          —<span className="deck__timer-total"> / {formatTimecode(durationUs)}</span>
        </span>
        <span className="deck__timer-label">take length</span>
      </div>
      <div className="deck__stats">
        <button type="button" className="deck__ghost deck__ghost--quiet" onClick={onNewTake}>
          New take
        </button>
      </div>
    </div>
  );
}

export function EditorDeck({
  player,
  durationUs,
  onNewTake,
  loop,
  onToggleLoop,
  frameUs,
}: {
  player: ClipPlayer;
  durationUs: number;
  onNewTake: () => void;
  loop: boolean;
  onToggleLoop: () => void;
  frameUs: number;
}) {
  const playerState = useStore(player.store);

  // Apply the persisted loop preference to this take's player exactly once.
  useMountEffect(() => {
    player.setLooping(loop);
  });

  const step = (frames: number) => player.stepFrames(frames, frameUs);
  const jump = (us: number) => {
    player.pause();
    player.seekUs(playerState.currentUs + us);
  };

  return (
    <div className="deck deck--editor">
      <div className="deck__primary">
        <button
          type="button"
          className="transport"
          onClick={() => player.toggle()}
          aria-label={playerState.playing ? 'Pause preview' : 'Play preview'}
        >
          {playerState.playing ? (
            <span className="transport__pause" aria-hidden="true" />
          ) : (
            <span className="transport__play" aria-hidden="true" />
          )}
        </button>
        <div className="transport__steps">
          <button type="button" className="deck__ghost" onClick={() => jump(-1_000_000)} aria-label="Back one second">
            −1s
          </button>
          <button type="button" className="deck__ghost" onClick={() => step(-1)} aria-label="Back one frame">
            −1f
          </button>
          <button type="button" className="deck__ghost" onClick={() => step(1)} aria-label="Forward one frame">
            +1f
          </button>
          <button type="button" className="deck__ghost" onClick={() => jump(1_000_000)} aria-label="Forward one second">
            +1s
          </button>
        </div>
        <button
          type="button"
          className={`deck__ghost${loop ? ' is-active' : ''}`}
          onClick={onToggleLoop}
          aria-pressed={loop}
        >
          Loop cut
        </button>
      </div>

      <div className="deck__timer">
        <span className="deck__timer-value">
          {formatTimecode(playerState.currentUs)}
          <span className="deck__timer-total"> / {formatTimecode(durationUs)}</span>
        </span>
        <span className="deck__timer-label">playhead</span>
      </div>

      <div className="deck__stats">
        <button type="button" className="deck__ghost deck__ghost--quiet" onClick={onNewTake}>
          New take
        </button>
      </div>
    </div>
  );
}
